import { translateBackend } from '@assets/i18n/config';
import { Clear } from '@mui/icons-material';
import {
  availableOperators,
  type ObjectType,
  type RuleDocument,
  type RuleOptions,
  ruleMapping,
  ValueTypes,
} from '@onevesthq/ov-conditions';
import { Jurisdictions } from '@onevesthq/ov-enums';
import { Box } from '@ovComponents/1-primative/box/box';
import { Grid } from '@ovComponents/1-primative/grid/grid';
import { DateField } from '@ovComponents/2-component/dateField/dateField';
import { IconButton } from '@ovComponents/2-component/iconButton/iconButton';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { AccountTypeSelect } from '@ovComponents/3-pattern/accountTypeSelect/accountTypeSelect';
import { useTranslation } from 'react-i18next';

const Field = ({
  rule,
  updateRule,
  valueType,
  availableRules,
}: {
  rule: RuleDocument;
  updateRule: (rule: RuleDocument) => void;
  objectType: ObjectType;
  valueType: ValueTypes;
  availableRules: RuleOptions[];
}) => {
  const { t } = useTranslation('components');
  const jurisdictions: Jurisdictions[] = Object.values(Jurisdictions);

  switch (valueType) {
    case ValueTypes.TEXT:
      return (
        <TextField
          value={rule.value}
          onChange={(e: any) => {
            const r = { ...rule, value: e.target.value };
            updateRule(r);
          }}
          label=''
          fullWidth
        />
      );
    case ValueTypes.NUMBER:
      return (
        <TextField
          type='number'
          value={rule.value}
          onChange={(e: any) => {
            const r = { ...rule, value: e.target.value };
            updateRule(r);
          }}
          label=''
          fullWidth
        />
      );
    case ValueTypes.DATE:
      return (
        <DateField
          value={rule.value}
          onChange={(e: any) => {
            const r = { ...rule, value: e };
            updateRule(r);
          }}
          label=''
          fullWidth
        />
      );
    case ValueTypes.SELECT:
    case ValueTypes.MULTI_SELECT:
      if (['in', 'notIn'].includes(rule.operator)) {
        return (
          <SelectField
            multiple
            value={Array.isArray(rule.value) ? rule.value : [rule.value]}
            onChange={(e: any) => {
              const r = { ...rule, value: e.target.value };
              updateRule(r);
            }}
            label=''
            fullWidth
          >
            {availableRules
              .find((x: RuleOptions) => x.name === rule.field)
              ?.options?.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
          </SelectField>
        );
      }
      return (
        <SelectField
          value={rule.value}
          onChange={(e: any) => {
            const r = { ...rule, value: e.target.value };
            updateRule(r);
          }}
          label=''
          fullWidth
        >
          {availableRules
            .find((x: RuleOptions) => x.name === rule.field)
            ?.options?.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
        </SelectField>
      );
    case ValueTypes.BOOLEAN:
      return <></>;
    case ValueTypes.ACCOUNT_TYPES:
      return (
        <AccountTypeSelect
          operator={rule.operator}
          value={rule.value}
          onChange={(e: any) => {
            const r = { ...rule, value: e };
            updateRule(r);
          }}
          label=''
          size='medium'
        />
      );
    case ValueTypes.JURISDICTION: {
      const isMultiple = ['in', 'notIn'].includes(rule.operator);
      const value = isMultiple
        ? Array.isArray(rule.value)
          ? rule.value
          : [rule.value]
        : rule.value;
      return (
        <SelectField
          multiple={isMultiple}
          value={value}
          fullWidth
          onChange={(e: any) => {
            const r = { ...rule, value: e.target.value };
            updateRule(r);
          }}
          label=''
        >
          {jurisdictions.map((jurisdiction: string) => (
            <MenuItem key={jurisdiction} value={jurisdiction}>
              {t(`geoNames:${jurisdiction}`)}
            </MenuItem>
          ))}
        </SelectField>
      );
    }
    default:
      return null;
  }
};

export const Rule = ({
  rule,
  updateRule,
  availableRules,
  objectType,
  removeRule,
}: {
  rule: RuleDocument;
  updateRule: (rule: RuleDocument) => void;
  availableRules: RuleOptions[];
  objectType: ObjectType;
  removeRule: () => void;
}) => {
  const { t } = useTranslation(['ruleFields', 'components']);

  return (
    <Box display='flex' flexDirection='row' gap={1} width='100%'>
      <Grid container spacing={1}>
        <Grid item xs={4}>
          <SelectField
            value={rule.field}
            onChange={(e: any) => {
              const ruleOption = availableRules.find(
                (x: RuleOptions) => x.name === e.target.value,
              );
              const r = {
                ...rule,
                field: e.target.value,
                value: '',
                customFieldKey: ruleOption?.customField?.key,
                customFieldType: ruleOption?.valueType,
              };
              updateRule(r);
            }}
            label=''
            fullWidth
          >
            {availableRules.map((ruleOption) => (
              <MenuItem key={ruleOption.name} value={ruleOption.name}>
                {ruleOption.customField
                  ? ruleOption.name.split('.').length > 1
                    ? `${t(ruleOption.name.split('.')[0])} ${translateBackend(ruleOption.customField?.translatedName)}`
                    : translateBackend(ruleOption.customField?.translatedName)
                  : ruleOption.name
                      .split('.')
                      .map((x) => t(x))
                      .join(' ')}
              </MenuItem>
            ))}
          </SelectField>
        </Grid>
        <Grid item xs={4}>
          <SelectField
            value={rule.operator}
            onChange={(e: any) => {
              const r = { ...rule, operator: e.target.value };
              updateRule(r);
            }}
            label=''
            fullWidth
          >
            {availableOperators(
              rule.customFieldType
                ? rule.customFieldType
                : ruleMapping[objectType]?.find(
                    (x: RuleOptions) => x.name === rule.field,
                  )?.valueType || ValueTypes.TEXT,
            ).map((operator) => (
              <MenuItem key={operator} value={operator}>
                {t(`components:ruleBuilder.operators.${operator}`)}
              </MenuItem>
            ))}
          </SelectField>
        </Grid>
        <Grid item xs={3}>
          <Field
            objectType={objectType}
            rule={rule}
            updateRule={updateRule}
            valueType={
              rule.customFieldType ||
              ruleMapping[objectType]?.find(
                (x: RuleOptions) => x.name === rule.field,
              )?.valueType ||
              ValueTypes.TEXT
            }
            availableRules={availableRules}
          />
        </Grid>
        <Grid item xs={1}>
          <IconButton onClick={removeRule}>
            <Clear />
          </IconButton>
        </Grid>
      </Grid>
    </Box>
  );
};
